'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, ShieldAlert } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter, useSearchParams } from 'next/navigation';
import { safeRedirectPath } from '@/lib/auth/utils';

interface FormData {
  email: string;
  password: string;
}

export default function AdminLoginForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { signIn, signOut, resolveRole } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>();

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    try {
      const result = await signIn(data.email, data.password);
      const role = await resolveRole(result.user);

      if (role !== 'admin') {
        await signOut();
        toast.error('Access denied — Admin accounts only.');
        return;
      }

      toast.success('Welcome back, DJ ExXtra! Loading Command Center...');
      router.push(safeRedirectPath(searchParams.get('redirect'), '/admin-dashboard'));
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Invalid credentials. Admin access only.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-5">
        <h2 className="text-xl font-bold text-foreground">DJ Command Center</h2>
        <p className="text-sm text-muted-foreground mt-1">Restricted to Christopher Harris — DJ ExXtra admin access only.</p>
      </div>

      <div className="mb-5 rounded-xl border border-purple-500/20 bg-purple-500/5 px-4 py-3 flex gap-2">
        <ShieldAlert size={14} className="shrink-0 mt-0.5" style={{ color: 'var(--neon-purple)' }} />
        <p className="text-xs text-muted-foreground leading-relaxed">
          This portal is exclusively for <span className="font-semibold text-foreground">Christopher Harris</span>. Unauthorized access is blocked by role-based middleware.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5">Admin Email</label>
          <input
            {...register('email', { required: 'Email is required', pattern: { value: /^\S+@\S+$/, message: 'Invalid email' } })}
            type="email"
            autoComplete="email"
            className="w-full px-3 py-2.5 rounded-lg bg-input border border-border text-sm text-foreground focus:outline-none transition-colors"
            style={{ '--tw-ring-color': 'var(--neon-purple)' } as React.CSSProperties}
            placeholder="admin@maximumeffortmdj.com"
          />
          {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5">Admin Password</label>
          <div className="relative">
            <input
              {...register('password', { required: 'Password is required' })}
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              className="w-full px-3 py-2.5 pr-10 rounded-lg bg-input border border-border text-sm text-foreground focus:outline-none transition-colors"
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
          {errors.password && <p className="text-xs text-red-400 mt-1">{errors.password.message}</p>}
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 rounded-xl text-sm font-bold transition-all duration-150 disabled:opacity-60 flex items-center justify-center gap-2 active:scale-95"
          style={{ background: 'var(--neon-purple)', color: '#fff' }}
        >
          {loading ? <><Loader2 size={15} className="animate-spin" /> Authenticating...</> : 'Enter Command Center'}
        </button>
      </form>
    </div>
  );
}
